import * as R from 'ramda'
import React, { useCallback, useEffect, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useFormContext, useWatch } from 'react-hook-form/dist/index.ie11'
import { useDispatch, useSelector } from 'react-redux'
import styled, { css } from 'styled-components'
import { v4 as uuidv4 } from 'uuid'

import AspectBox from '../../components/AspectBox'
import {
  ErrorLabel,
  FieldComponent,
  HelpText,
  OutsideLabel as Label
} from '../../components/Form/shared'
import { Col, Row } from '../../components/Layout/FlexGrid'
import Picture from '../../components/Picture'
import SvgIcon from '../../components/SvgIcon'
import { MEDIA_CONDITIONS } from '../../constants'
import { useMq, usePrev } from '../../hooks'
import { useTranslation } from '../../i18n'
import AddSvg from '../../public/static/images/icon-add.svg'
import DeleteSvg from '../../public/static/images/icon-delete.svg'
import RetrySvg from '../../public/static/images/icon-retry.svg'
import { uploadRequest, uploadRetry } from '../../redux/file/actions'
import { getColor, getFontSize, objectFit } from '../../utils/cssUtils'
import { isExist } from '../../utils/ramdaUtils'

const DropzoneInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`
const DropzoneText = styled.span`
  margin-left: 4px;
  font-size: ${getFontSize('body1')};
  text-align: left;
`

const DropzoneContent = props => {
  const { t } = useTranslation('common')

  const isAppview = useSelector(R.path(['appState', 'isAppview']))

  return (
    <>
      <DropzoneInner>
        <SvgIcon svg={<AddSvg />} />
        <DropzoneText>
          {isAppview
            ? t('personalFundraising.create.form.titleImage.uploadMobile')
            : t('personalFundraising.create.form.titleImage.upload')}
        </DropzoneText>
      </DropzoneInner>
    </>
  )
}

const UploadZone = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 20px;
  border-width: 1px;
  border-style: dashed;
  border-radius: 4px;
  background-color: ${getColor('input', 0.04)};
  color: ${getColor('black')};
  outline: none;
  transition: all 0.3s ease-in-out;
  font-size: ${getFontSize('body1')};
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  text-align: center;
  cursor: pointer;
  ${({ disabled, isDragReject, isDragAccepted, isDragActive }) => {
    if (disabled || isDragReject) {
      return css`
        color: ${getColor('lightGrey')};
        background-color: transparent;
        border-color: ${getColor('lightGrey', 0.2)};
        cursor: not-allowed;
      `
    } else if (isDragActive || isDragAccepted) {
      return css`
        background-color: transparent;
        border-color: ${getColor('pink')};
      `
    } else {
      return css`
        &:hover,
        &:focus {
          background-color: transparent;
          border-color: ${getColor('pink')};
        }
      `
    }
  }}

  @media (max-width: 400px) {
    padding: 4px;
  }
`
const DropzoneContentWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`
const StyledAspectBox = styled(AspectBox)`
  background: none;
`
const ImgAspectBox = styled(AspectBox)`
  &::after {
    content: '';
    display: block;
    width: 100%;
    height: 32px;
    position: absolute;
    bottom: 0;
    left: 0;
    background-image: linear-gradient(rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.7));
    border-radius: 0 0 4px 4px;
  }
  img {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    border-radius: 4px;
    object-fit: cover;
    ${objectFit('cover')}
  }
`
const DeleteBtn = styled.button`
  position: absolute;
  bottom: 6px;
  right: 6px;
  z-index: 2;
  appearance: none;
  background: transparent;
  border: none;
  padding: 0;
  line-height: 1;
  cursor: pointer;
  outline: none;
`
const DeleteIcon = styled(SvgIcon)`
  ${DeleteBtn}:hover &,
  ${DeleteBtn}:focus & {
    svg {
      transition: color 0.3s ease-in-out;
      color: ${getColor('theme')};
    }
  }
`
const UploadProgress = styled.div`
  position: absolute;
  z-index: 1;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.7);
  border-radius: 4px;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-flow: row wrap;
  align-content: center;
  > * {
    width: 100%;
  }
  opacity: ${({ show }) => (show ? 1 : 0)};
  pointer-events: ${({ show }) => (show ? 'auto' : 'none')};
  transition: opacity 0.2s ease-in-out;
`
const UploadText = styled.span`
  text-align: center;
`
const ProgressBar = styled.div`
  position: relative;
  height: 2px;
  width: calc(100% - 60px);
  margin: 16px 30px 0;
  background-color: #fff;
  border-radius: 1px;
  overflow: hidden;
  &::after {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    background-color: ${getColor('pink')};
    width: 100%;
    transform: translate3d(
      ${({ progress }) =>
        progress ? `${-100 + Math.round(progress * 100)}%` : `-100%`},
      0,
      0
    );
    transition: transform 0.2s ease-in-out;
  }
`
const Retry = styled.div`
  position: absolute;
  z-index: 1;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.7);
  border-radius: 4px;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-flow: row wrap;
  align-content: center;
  border: 1px solid ${getColor('error')};
`
const RetrySvgIcon = styled(SvgIcon)`
  margin-right: 8px;
`
const RetryContent = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  text-decoration: underline;
  &:hover {
    color: #fff;
  }
`

const Dropzone = ({
  name,
  label,
  helpText,
  validationRules,
  disabled,
  dropzoneContent = <DropzoneContent />,
  maxUploadCount = 10,
  multiple = true,
  ...props
}) => {
  const {
    getValues,
    register,
    unregister,
    setValue,
    trigger,
    errors
  } = useFormContext()

  const { t } = useTranslation('common')

  const dispatch = useDispatch()

  let savedList = useWatch({ name, defaultValue: undefined })

  savedList = R.ifElse(
    R.either(R.isNil, R.isEmpty),
    R.always(null),
    R.pipe(R.of, R.flatten, R.reject(R.propEq('fileId', undefined)))
  )(savedList)

  const files = useSelector(
    R.path(['fileState', 'entities', 'files']),
    R.equals
  )

  const { isMobile } = useMq()

  const hasError = !R.isNil(errors[name])

  const [uploadedImgs, setUploadedImgs] = useState([])

  const uploadedList = R.unless(
    R.isNil,
    R.pipe(
      R.pick(R.pluck('key', uploadedImgs)),
      R.values,
      R.project(['fileId', 'url', 'filename', 'key', 'progress']),
      R.reject(R.propEq('fileId', undefined))
    )
  )(files)

  const prevUploadedList = usePrev(uploadedList)

  const prevSavedList = usePrev(savedList)

  const prevUploadedImgs = usePrev(uploadedImgs)

  const onDrop = useCallback(
    acceptedFiles => {
      acceptedFiles.forEach(file => {
        const form = new window.FormData()
        const key = uuidv4()
        form.append('file', file)
        dispatch(uploadRequest(form, key))
        const reader = new window.FileReader()
        reader.onload = () => {
          const { result } = reader
          setUploadedImgs(prevUploadedImgs =>
            R.takeLast(
              maxUploadCount,
              R.prepend({ dataURL: result, key, file: form }, prevUploadedImgs)
            )
          )
        }
        reader.readAsDataURL(file)
      })
    },
    [dispatch, maxUploadCount]
  )

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject
  } = useDropzone({
    disabled: R.length(uploadedImgs) === maxUploadCount || disabled,
    multiple,
    accept: 'image/png, image/jpeg',
    maxSize: 10 * 1024 * 1024,
    onDrop
  })

  const handleDelete = i => e => {
    setUploadedImgs(R.remove(i, 1, uploadedImgs))
    trigger(name)
  }

  const handleRetry = (file, key) => e => {
    e.preventDefault()
    dispatch(uploadRetry(file, key))
  }

  const preview = uploadedImgs.map(({ dataURL, url, key, file }, i) => {
    const progress = R.pathOr(0, [key, 'progress'], files)

    const isUploading = files
      ? R.pipe(R.path([key, 'fileId']), R.isNil)(files)
      : true

    const fileHasError = !R.isNil(R.path([key, 'error'], files))

    return (
      <Col key={i} xl={6} md={8} xs={12}>
        <ImgAspectBox>
          <Picture
            image={dataURL || url}
            sizes={`${MEDIA_CONDITIONS.DESKTOP} 180px, ${MEDIA_CONDITIONS.TABLET} calc(616px / 3), calc(50vw - 24px)`}
          />
          <DeleteBtn type='button' onClick={handleDelete(i)}>
            <DeleteIcon svg={<DeleteSvg />} color='#fff' />
          </DeleteBtn>
          {file &&
            (fileHasError ? (
              <Retry>
                <RetryContent onClick={handleRetry(file, key)}>
                  <RetrySvgIcon svg={<RetrySvg />} />
                  {t('common.retry')}
                </RetryContent>
              </Retry>
            ) : (
              <UploadProgress show={isUploading}>
                <UploadText>{t('common.uploading')}</UploadText>
                <ProgressBar progress={progress} />
              </UploadProgress>
            ))}
        </ImgAspectBox>
      </Col>
    )
  })

  useEffect(() => {
    register({ name }, validationRules)
    return () => unregister(name)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [register])

  useEffect(() => {
    // react-hook-form cache the validationRules, therefore need to register again when the rules change
    const value = getValues(name)

    register({ name }, validationRules) // register cause the field to lose its value

    setValue(name, value)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationRules])

  useEffect(() => {
    if (
      !R.equals(prevSavedList, savedList) &&
      R.isNil(prevSavedList) &&
      isExist(savedList) &&
      R.isEmpty(
        R.intersection(
          R.pluck('fileId', savedList || []),
          R.pluck('fileId', uploadedList || [])
        )
      ) // should not be just uploaded
    ) {
      R.forEach(saved => {
        setUploadedImgs(prevUploaded => R.prepend(saved, prevUploaded))
      }, savedList)
    }

    // setValue('corporateCreatorLogoImage', null)
    if (
      !R.equals(prevSavedList, savedList) &&
      !R.isNil(prevSavedList) &&
      R.isNil(savedList)
    ) {
      setUploadedImgs([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prevSavedList, savedList])

  useEffect(() => {
    if (!R.equals(prevUploadedList, uploadedList)) {
      R.forEach(uploaded => {
        setUploadedImgs(prevUploaded =>
          R.adjust(
            R.findIndex(R.propEq('key', uploaded.key))(prevUploaded),
            R.mergeRight(R.__, uploaded),
            prevUploaded
          )
        )
      }, uploadedList)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prevUploadedList, uploadedList])

  useEffect(() => {
    if (!R.equals(prevUploadedImgs, uploadedImgs)) {
      const value = R.pipe(
        R.project(['fileId', 'url', 'filename']),
        R.reject(R.propEq('fileId', undefined))
      )(uploadedImgs)

      const val = multiple ? value : R.defaultTo(null)(R.head(value))

      setValue(name, val) // need default to null in order to delete the record in db

      trigger(name)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prevUploadedImgs, uploadedImgs, multiple])

  if (R.isNil(isMobile)) return null

  return (
    <FieldComponent>
      {label && <Label>{label}</Label>}
      <Row gutter={{ xl: 24, md: 24, xs: 16 }}>
        {isMobile && preview}
        <Col xl={6} md={8} xs={12}>
          <StyledAspectBox>
            <UploadZone
              {...getRootProps({
                isDragActive,
                isDragAccept,
                isDragReject,
                disabled: R.length(uploadedImgs) === maxUploadCount || disabled
              })}
            >
              <input {...getInputProps()} />
              <DropzoneContentWrapper>{dropzoneContent}</DropzoneContentWrapper>
            </UploadZone>
          </StyledAspectBox>
        </Col>
        {!isMobile && preview}
      </Row>
      {helpText && <HelpText>{helpText}</HelpText>}
      {hasError && <ErrorLabel>{R.path([name, 'message'], errors)}</ErrorLabel>}
    </FieldComponent>
  )
}

export default Dropzone
